import { Button, Flex, Heading, Text, Textarea, VStack } from '@chakra-ui/react'
import { FormEvent, useState } from 'react'

import { CharCount } from '../../../components/CharCount'
import { DetailField } from '../../../components/DetailField'
import { FormField } from '../../../components/FormField'
import { ListInput } from '../../../components/ListInput'
import { IPersonnelMovementRequestJobPosition } from '../../../services/PersonnelMovementRequests/interfaces'
import { LIST_FIELD_LABELS, LIST_FIELDS } from '../JobPositions/constants'

// Same limit as the API.
const CHANGES_MAX_LENGTH = 2000

interface IPersonnelMovementDescriptiveStepProps {
  jobPosition: IPersonnelMovementRequestJobPosition
  /** "Seguir com a abertura usando este descritivo". */
  onKeep: () => Promise<void>
  /** "Solicitar alterações ao RH", with what the requester wants changed. */
  onAskChanges: (changes: string) => Promise<void>
  onBack: () => void
}

/**
 * The description of the role picked in the MP, for the requester to confirm before opening it,
 * or to ask RH for changes.
 */
export function PersonnelMovementDescriptiveStep({
  jobPosition,
  onKeep,
  onAskChanges,
  onBack
}: IPersonnelMovementDescriptiveStepProps) {
  const [askingChanges, setAskingChanges] = useState(false)
  const [changes, setChanges] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const run = async (action: () => Promise<void>) => {
    setSaving(true)
    try {
      await action()
    } finally {
      setSaving(false)
    }
  }

  const handleSendChanges = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (changes.trim() === '') {
      setError('Descreva as alterações que você precisa.')
      return
    }
    run(() => onAskChanges(changes.trim()))
  }

  return (
    <>
      <Heading as="h2" mb="2">
        Descritivo do cargo
      </Heading>
      <Text mb="6">
        Confira o descritivo de <b>{jobPosition.name}</b>. Se estiver de acordo, siga com a abertura. Se precisar de
        ajustes, conte ao RH o que mudar.
      </Text>

      <VStack align="stretch" spacing="5" mb="8">
        {LIST_FIELDS.map((field) => (
          <DetailField key={field} label={LIST_FIELD_LABELS[field]}>
            <ListInput value={jobPosition[field] ?? []} onChange={() => undefined} isReadOnly />
          </DetailField>
        ))}
      </VStack>

      {askingChanges ? (
        // The panel's own message replaces the browser bubble for the required text.
        <form onSubmit={handleSendChanges} noValidate>
          <FormField label="Quais alterações você precisa?" error={error} isRequired mb="4">
            <Textarea
              value={changes}
              onChange={(event) => {
                setChanges(event.target.value)
                setError('')
              }}
              maxLength={CHANGES_MAX_LENGTH}
              rows={5}
              autoFocus
            />
            <CharCount value={changes} max={CHANGES_MAX_LENGTH} />
          </FormField>
          <Flex gap="3" justify="space-between" wrap="wrap">
            <Button variant="ghost" onClick={() => setAskingChanges(false)} isDisabled={saving}>
              Cancelar
            </Button>
            <Button colorScheme="magenta" type="submit" isLoading={saving}>
              Enviar ao RH
            </Button>
          </Flex>
        </form>
      ) : (
        <Flex gap="3" justify="space-between" wrap="wrap">
          <Button colorScheme="magenta" variant="outline" onClick={onBack} isDisabled={saving}>
            Voltar
          </Button>
          <Flex gap="3" wrap="wrap">
            <Button variant="outline" onClick={() => setAskingChanges(true)} isDisabled={saving}>
              Solicitar alterações ao RH
            </Button>
            <Button colorScheme="magenta" onClick={() => run(onKeep)} isLoading={saving}>
              Seguir com a abertura usando este descritivo
            </Button>
          </Flex>
        </Flex>
      )}
    </>
  )
}
